/**
 * Textarea Adapter for shadcn
 * 
 * This component adapts the shadcn textarea to fit our design system.
 */
import * as React from "react"
import { Textarea as ShadcnTextarea, type WebTextareaProps } from "../textarea"
import { cn } from "@/lib/utils"

// Define properties specific to our premium textarea implementation
export interface TextareaProps extends WebTextareaProps {
  // Additional app-specific props can be added here
  helperText?: string;
  state?: 'error' | 'success' | 'warning';
}

/**
 * Textarea adapter for shadcn
 * 
 * This component adapts the shadcn textarea to fit our design system.
 * 
 * @example
 * ```tsx
 * <Textarea 
 *   variant="default"
 *   placeholder="Enter text"
 *   rows={4}
 * />
 * ```
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({
    className,
    variant = "default",
    state,
    label,
    helperText,
    error,
    disabled,
    value,
    defaultValue,
    onValueChange,
    onChange,
    required,
    id,
    radius,
    ...props
  }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange?.(e);
      onValueChange?.(e.target.value);
    };


    return (
      <div className="relative w-full">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <ShadcnTextarea
          ref={ref}
          variant={variant}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
          required={required}
          id={id}
          radius={radius}
          {...props}
        />
        {(helperText || error) && (
          <p className={cn(
            "mt-1 text-sm",
            error ? "text-red-500" : "text-gray-500 dark:text-gray-400"
          )}>
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
